import type { MRT_ColumnDef } from '@talentmesh/table';
import React, { useMemo } from 'react';
import UIStrings from '@/Utils/UIStrings';
import { ShortlistedProfile } from '../../../../api/DTO/ShortlistedProfile';
import CompanyCell from '../../../TableCells/CompanyCell';
import StatusCell from './StatusCell';
import ContactInfoCell from './ContactInfoCell';
import { useOutreachCandidatePopoutContext } from '../../Outreach/OutreachContext';
import toDateString from '@/Utils/DateHelper';
import TotalEmailsCell from './TotalEmailsCell';
import EmailRepliedCell from './EmailRepliedCell';
import CandidateCell from './CandidateCell';
import simplifyShortlistedProfile from '../simplifyShortlistedProfile';
import TextCell from '@/Pages/Candidates/Overview/Contexts/CandidatesTableContext/cells/TextCell';
import ScoreCell from '@/Components/Table/ScoreCell';

export type ShortlistedCandidatesCol = MRT_ColumnDef<ShortlistedProfile>;
export type CellParams = Parameters<Extract<NonNullable<ShortlistedCandidatesCol['Cell']>, Function>>['0'];

const useColumns = () => {
    const { openSendEmailPopout: openOutreachPopout } = useOutreachCandidatePopoutContext();

    return useMemo(() => {
        const columns: ShortlistedCandidatesCol[] = [
            {
                id: 'name',
                accessorFn: (row) => row.profile.name,
                header: UIStrings.Candidate,
                Cell: ({ row }: CellParams) => <CandidateCell {...row.original} />,
                size: 200,
            },
            {
                id: 'contactInfo',
                header: UIStrings.ContactInfo,
                Cell: ({ row }: CellParams) => (
                    <ContactInfoCell
                        {...row.original}
                        onMailClick={
                            !row.original.emailSentCount ? () => openOutreachPopout([row.original.profile]) : 'disabled'
                        }
                    />
                ),
                size: 150,
            },
            {
                id: 'status',
                header: UIStrings.Status,
                Cell: (params: CellParams) => <StatusCell {...params} />,
                size: 220,
            },
            {
                id: 'company',
                header: UIStrings.Company,
                Cell: ({ row }: CellParams) => <CompanyCell {...row.original} />,
                size: 220,
            },
            {
                id: 'position',
                accessorFn: (row) => row.profile.position,
                header: UIStrings.JobTitle,
                Cell: ({ row }: CellParams) => <TextCell value={simplifyShortlistedProfile(row.original).jobTitle} />,
                size: 400,
            },
            {
                id: 'location',
                accessorFn: (row) => row.profile.currentJobLocationGeocode?.title,
                header: UIStrings.Location,
                Cell: ({ row, renderedCellValue }: CellParams) => (
                    <TextCell
                        value={(renderedCellValue as string) || row.original.profile.locationGeocode?.title || ''}
                    />
                ),
                size: 270,
            },
            {
                id: 'matchScore',
                header: UIStrings.MatchScore,
                Cell: ({ row }: CellParams) => (
                    <ScoreCell level={row.original.matchScore?.scoreLevel} score={row.original.matchScore?.score} />
                ),
                size: 140,
            },
            {
                id: 'emailSentCount',
                header: UIStrings.TotalEmails,
                accessorFn: ({ emailSentCount }) => emailSentCount ?? '-',
                Cell: ({ renderedCellValue }: CellParams) => <TotalEmailsCell value={renderedCellValue} />,
                size: 140,
            },
            {
                id: 'lastEmailAt',
                header: UIStrings.LastEmail,
                accessorFn: ({ lastEmailAt }) => (lastEmailAt ? toDateString(new Date(lastEmailAt)) : '-'),
                Cell: ({ renderedCellValue }: CellParams) => <TextCell value={renderedCellValue as string} />,
                size: 130,
            },
            {
                id: 'repliedAt',
                header: UIStrings.Replied,
                Cell: (params: CellParams) => <EmailRepliedCell {...params} />,
                size: 130,
            },
        ];

        return columns;
    }, [openOutreachPopout]);
};

export default useColumns;
